"use client"

import {
  ANON_ID_COOKIE_MAX_AGE_SECONDS,
  buildAnonymousIdSetCookieValue,
  isPlausibleAnonymousId,
  readIdFromDocumentCookie,
} from "./anon-id-cookie"
import { captureBrowserAttributionIds } from "./browser-ids"
import { persistFbcDocumentCookie } from "./fbc-cookie"
import { buildSignupBrowserIdOverlay } from "./signup-browser-overlay"
import {
  extractUtmClickBundleFromUrl,
  mergeMarketingParamsIntoRawQuery,
  mergeUtmClickBundles,
} from "./extract-utm-from-url"
import { isRelevantVisit, normalizeAttributionPayload } from "./normalization"
import {
  mergeProgramChannelIntoUtmFields,
  resolveProgramChannelFromVisit,
} from "./program-channel"
import {
  acquisitionFieldsFromUtmLike,
  persistStickyAcquisitionCampaign,
  readStickyAcquisitionCampaign,
} from "@/lib/attribution/sticky-acquisition-client"
import type {
  ClientStoredState,
  RawAttributionPayload,
  SignupAttributionContext,
  TrackTouchResponse,
} from "./types"
import { PARTNER_LS_KEY } from "@/lib/partners/constants"
import {
  normalizePartnerSlug,
  readPartnerSlugFromDocumentCookie,
} from "@/lib/partners/partner-slug-cookie"
import { REFERRAL_LS_KEY } from "@/lib/referral/constants"
import { normalizeReferralCode, readReferralCodeFromDocumentCookie } from "@/lib/referral/referral-code-cookie"

const ANON_KEY = "attribution_anonymous_id"
const SESSION_KEY = "attribution_session_id"
const SESSION_STARTED_AT_KEY = "attribution_session_started_at"
const LAST_ACTIVITY_AT_KEY = "attribution_last_activity_at"
const LANDING_PAGE_KEY = "attribution_landing_page"
const LAST_PAYLOAD_KEY = "attribution_last_payload"
const STATE_KEY = "attribution_state"
const CONSTRUCTED_FBC_KEY = "attribution_constructed_fbc"

const SESSION_INACTIVITY_MS = 30 * 60 * 1000 // 30 minutos
const CLIENT_DEDUPE_WINDOW_MS = 60 * 1000 // 60 segundos

function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined"
}

export function readStoredConstructedFbc(): string | null {
  return safeGetLocalStorage()?.getItem(CONSTRUCTED_FBC_KEY) ?? null
}

function safeGetLocalStorage(): Storage | null {
  if (!isBrowser()) return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function generateId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(8)
    crypto.getRandomValues(bytes)
    return `${Date.now().toString(36)}-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`
  }
  throw new Error("Secure random generator unavailable")
}

function getNumberFromStorage(storage: Storage, key: string): number | null {
  const raw = storage.getItem(key)
  if (!raw) return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  return n
}

function persistAnonymousIdCookie(id: string): void {
  if (!isBrowser()) return
  if (!isPlausibleAnonymousId(id)) return
  let isSecure = false
  try {
    isSecure = window.location.protocol === "https:"
  } catch {
    // ignore
  }
  try {
    document.cookie = buildAnonymousIdSetCookieValue(id.trim(), ANON_ID_COOKIE_MAX_AGE_SECONDS, isSecure)
  } catch {
    // ignore
  }
}

function ensureAnonymousId(): string {
  const storage = safeGetLocalStorage()

  const fromLs = storage?.getItem(ANON_KEY)?.trim()
  if (fromLs && isPlausibleAnonymousId(fromLs)) {
    persistAnonymousIdCookie(fromLs)
    return fromLs
  }

  try {
    const fromCookie = readIdFromDocumentCookie(document.cookie)
    if (fromCookie) {
      try {
        storage?.setItem(ANON_KEY, fromCookie)
      } catch {
        // localStorage indisponível; cookie continua sendo a fonte
      }
      persistAnonymousIdCookie(fromCookie)
      return fromCookie
    }
  } catch {
    // document.cookie ilegível em alguns ambientes
  }

  const id = generateId()
  try {
    storage?.setItem(ANON_KEY, id)
  } catch {
    // ignore
  }
  persistAnonymousIdCookie(id)
  return id
}

function ensureSessionId(): string {
  const storage = safeGetLocalStorage()
  if (!storage) {
    return generateId()
  }

  const now = Date.now()
  const existingSessionId = storage.getItem(SESSION_KEY)
  const lastActivityAt = getNumberFromStorage(storage, LAST_ACTIVITY_AT_KEY)

  let sessionId = existingSessionId

  if (!sessionId || !lastActivityAt || now - lastActivityAt > SESSION_INACTIVITY_MS) {
    // Nova sessão após mais de 30 minutos de inatividade (ou ausência de dados)
    sessionId = generateId()
    storage.setItem(SESSION_KEY, sessionId)
    storage.setItem(SESSION_STARTED_AT_KEY, String(now))
    storage.setItem(LAST_ACTIVITY_AT_KEY, String(now))
    // landing_page = primeira URL da sessão atual
    if (isBrowser()) {
      storage.setItem(LANDING_PAGE_KEY, window.location.href)
    }
  } else {
    // Sessão atual ainda é válida; apenas atualiza last_activity_at
    storage.setItem(LAST_ACTIVITY_AT_KEY, String(now))
  }

  return sessionId
}

function readStoredReferralCodeForAttribution(): string | null {
  const storage = safeGetLocalStorage()
  const fromLs = normalizeReferralCode(storage?.getItem(REFERRAL_LS_KEY) ?? null)
  if (fromLs) return fromLs
  if (!isBrowser()) return null
  return readReferralCodeFromDocumentCookie(document.cookie)
}

function readStoredPartnerSlugForAttribution(): string | null {
  const storage = safeGetLocalStorage()
  const fromLs = normalizePartnerSlug(storage?.getItem(PARTNER_LS_KEY) ?? null)
  if (fromLs) return fromLs
  if (!isBrowser()) return null
  return readPartnerSlugFromDocumentCookie(document.cookie)
}

function getLandingPage(): string | null {
  const storage = safeGetLocalStorage()
  if (!storage) return isBrowser() ? window.location.href : null
  const fromStorage = storage.getItem(LANDING_PAGE_KEY)
  if (fromStorage) return fromStorage
  if (isBrowser()) {
    const current = window.location.href
    storage.setItem(LANDING_PAGE_KEY, current)
    return current
  }
  return null
}

function buildRawPayload(): RawAttributionPayload | null {
  if (!isBrowser()) return null

  const storage = safeGetLocalStorage()
  const anonymousId = ensureAnonymousId()
  const sessionId = ensureSessionId()

  const url = new URL(window.location.href)
  const referrerUrl = document.referrer || null
  const currentPage = window.location.href
  const landingPage = getLandingPage()

  const rawQuery: Record<string, string | string[]> = {}
  url.searchParams.forEach((value, key) => {
    if (Object.prototype.hasOwnProperty.call(rawQuery, key)) {
      const current = rawQuery[key]
      if (Array.isArray(current)) {
        current.push(value)
      } else {
        rawQuery[key] = [current, value]
      }
    } else {
      rawQuery[key] = value
    }
  })

  let fromPage = extractUtmClickBundleFromUrl(url)
  let mergedRaw = rawQuery

  if (referrerUrl) {
    try {
      const refUrl = new URL(referrerUrl)
      const fromReferrer = extractUtmClickBundleFromUrl(refUrl)
      fromPage = mergeUtmClickBundles(fromPage, fromReferrer)
      mergedRaw = mergeMarketingParamsIntoRawQuery(rawQuery, refUrl)
    } catch {
      // referrer inválido ou opaco — ignora fallback
    }
  }

  const programChannel = resolveProgramChannelFromVisit({
    pageUrl: url,
    pathname: url.pathname,
    storedReferralCode: readStoredReferralCodeForAttribution(),
    storedPartnerSlug: readStoredPartnerSlugForAttribution(),
  })

  const withProgramChannel = mergeProgramChannelIntoUtmFields(
    {
      source: fromPage.source,
      medium: fromPage.medium,
      campaign: fromPage.campaign,
      gclid: fromPage.gclid,
      fbclid: fromPage.fbclid,
      ttclid: fromPage.ttclid,
    },
    programChannel,
  )

  persistStickyAcquisitionCampaign(acquisitionFieldsFromUtmLike(withProgramChannel))

  const sticky = readStickyAcquisitionCampaign()
  const effectiveFbclid =
    withProgramChannel.fbclid ?? fromPage.fbclid ?? sticky?.fbclid ?? null

  const browserIds = captureBrowserAttributionIds({
    cookieHeader: document.cookie,
    fbclid: effectiveFbclid,
    storedFbc: storage?.getItem(CONSTRUCTED_FBC_KEY),
    // Primeira observação do fbclid nesta visita. O valor fica no storage e não é reescrito depois.
    nowMs: Date.now(),
  })
  if (browserIds.fbc) {
    try {
      storage?.setItem(CONSTRUCTED_FBC_KEY, browserIds.fbc)
    } catch {
      // ignore
    }
    persistFbcDocumentCookie(browserIds.fbc)
  }

  const payload: RawAttributionPayload = {
    anonymousId,
    sessionId,
    source: withProgramChannel.source,
    medium: withProgramChannel.medium,
    campaign: withProgramChannel.campaign,
    content: fromPage.content,
    term: fromPage.term,
    gclid: withProgramChannel.gclid ?? sticky?.gclid ?? null,
    fbclid: effectiveFbclid,
    ttclid: withProgramChannel.ttclid ?? sticky?.ttclid ?? null,
    gbraid: fromPage.gbraid,
    wbraid: fromPage.wbraid,
    fbp: browserIds.fbp,
    fbc: browserIds.fbc,
    gaClientId: browserIds.gaClientId,
    referrerUrl,
    landingPage,
    currentPage,
    rawQuery: mergedRaw,
    stickyAcquisition: readStickyAcquisitionCampaign(),
  }

  return payload
}

function getClientLastPayload(): { fingerprint: string; sentAt: number } | null {
  const storage = safeGetLocalStorage()
  if (!storage) return null

  const raw = storage.getItem(LAST_PAYLOAD_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { fingerprint?: string; sentAt?: number }
    if (!parsed || typeof parsed.fingerprint !== "string" || typeof parsed.sentAt !== "number") return null
    return { fingerprint: parsed.fingerprint, sentAt: parsed.sentAt }
  } catch {
    return null
  }
}

function setClientLastPayload(fingerprint: string, sentAt: number): void {
  const storage = safeGetLocalStorage()
  if (!storage) return
  const payload = { fingerprint, sentAt }
  try {
    storage.setItem(LAST_PAYLOAD_KEY, JSON.stringify(payload))
  } catch {
    // ignore
  }
}

/** `fbc` construído inclui timestamp; identidade do clique já está em `fbclid`. */
function computeClientFingerprint(payload: RawAttributionPayload): string {
  const canonical = {
    source: payload.source ?? null,
    medium: payload.medium ?? null,
    campaign: payload.campaign ?? null,
    content: payload.content ?? null,
    term: payload.term ?? null,
    gclid: payload.gclid ?? null,
    fbclid: payload.fbclid ?? null,
    ttclid: payload.ttclid ?? null,
    gbraid: payload.gbraid ?? null,
    wbraid: payload.wbraid ?? null,
    fbp: payload.fbp ?? null,
    gaClientId: payload.gaClientId ?? null,
    referrerUrl: payload.referrerUrl ?? null,
    landingPage: payload.landingPage ?? null,
    currentPage: payload.currentPage ?? null,
  }
  return JSON.stringify(canonical)
}

function storeStateFromResponse(response: TrackTouchResponse, anonymousId: string, sessionId: string): void {
  const storage = safeGetLocalStorage()
  if (!storage) return

  const state: ClientStoredState = {
    anonymousId,
    sessionId,
    firstTouchId: response.firstTouchId,
    lastTouchId: response.lastTouchId,
  }

  try {
    storage.setItem(STATE_KEY, JSON.stringify(state))
  } catch {
    // ignore
  }
}

export function getAttributionIds(): { anonymousId: string; sessionId: string } {
  const anonymousId = ensureAnonymousId()
  const sessionId = ensureSessionId()
  return { anonymousId, sessionId }
}

/**
 * Recaptura `_ga` / `_fbp` / `_fbc` no submit. O bootstrap one-shot costuma
 * correr antes do GTM gravar os cookies.
 */
export function getSignupAttributionRequestBody(): {
  anonymousId: string
  sessionId: string
  context: SignupAttributionContext
} {
  const { anonymousId, sessionId } = getAttributionIds()
  if (!isBrowser()) {
    return { anonymousId, sessionId, context: {} }
  }

  const storage = safeGetLocalStorage()
  const context = buildSignupBrowserIdOverlay({
    cookieHeader: document.cookie,
    pageUrl: window.location.href,
    storedFbc: storage?.getItem(CONSTRUCTED_FBC_KEY),
  })
  if (context.fbc) {
    try {
      storage?.setItem(CONSTRUCTED_FBC_KEY, context.fbc)
    } catch {
      // ignore
    }
  }

  return { anonymousId, sessionId, context }
}

export function getAttributionState(): ClientStoredState | null {
  const storage = safeGetLocalStorage()
  if (!storage) {
    const { anonymousId, sessionId } = getAttributionIds()
    return { anonymousId, sessionId }
  }

  const raw = storage.getItem(STATE_KEY)
  if (!raw) {
    const { anonymousId, sessionId } = getAttributionIds()
    return { anonymousId, sessionId }
  }

  try {
    const parsed = JSON.parse(raw) as ClientStoredState
    if (!parsed || !parsed.anonymousId || !parsed.sessionId) {
      const { anonymousId, sessionId } = getAttributionIds()
      return { anonymousId, sessionId }
    }

    // Garante que a sessão atual ainda siga as regras de inatividade
    const sessionId = ensureSessionId()
    return {
      ...parsed,
      sessionId,
    }
  } catch {
    const { anonymousId, sessionId } = getAttributionIds()
    return { anonymousId, sessionId }
  }
}

export async function initAttribution(): Promise<void> {
  if (!isBrowser()) return

  const raw = buildRawPayload()
  if (!raw) return

  const normalized = normalizeAttributionPayload(raw)
  const relevant = isRelevantVisit(normalized)
  if (!relevant) return

  const fingerprint = computeClientFingerprint(raw)
  const last = getClientLastPayload()
  const now = Date.now()

  if (last && last.fingerprint === fingerprint && now - last.sentAt <= CLIENT_DEDUPE_WINDOW_MS) {
    return
  }

  try {
    const response = await fetch("/api/attribution/touch", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(raw),
    })

    if (!response.ok) {
      // Em caso de erro, não persistimos o last_payload para permitir nova tentativa depois
      console.warn("[attribution.client] Falha ao enviar touch:", response.status)
      return
    }

    const data = (await response.json().catch(() => null)) as TrackTouchResponse | null
    setClientLastPayload(fingerprint, now)

    if (data && data.firstTouchId && data.lastTouchId) {
      storeStateFromResponse(data, raw.anonymousId, raw.sessionId)
    }
  } catch (error) {
    console.warn("[attribution.client] Erro inesperado ao enviar touch:", error)
  }
}

